'use client';

import { Drakkar, IconCloseWhite } from '@mv/icons/web';
import { DismissableLayer, Flex, Heading, Paragraph } from '@mv/web-ui';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { match } from 'ts-pattern';
import { useI18n } from '@/modules/i18n/useI18n';
import { useScrollLock } from '@/modules/utils/useScrollLock';
import RichText from '@/payload/components/RichText/RichText';
import useSidePanel from '@/payload/components/Sidepanel/hooks/useSidePanel';
import type { Sidepanel as SidepanelDocument } from '@/payload/types';
import { SidepanelContentSkeleton, SidepanelHeaderSkeleton } from './SidepanelSkeleton';
import styles from './Sidepanel.module.scss';

type SidepanelProps = {
	preloaded?: Record<string, SidepanelDocument>;
};

export default function Sidepanel({ preloaded }: SidepanelProps) {
	const { terms } = useI18n();
	const searchParams = useSearchParams();
	const pathname = usePathname();

	const { locale } = useI18n();
	const sidepanelSlug = searchParams.get('spc');
	const preloadedSidepanel = sidepanelSlug ? (preloaded?.[sidepanelSlug] ?? undefined) : undefined;
	const [isVisible, setIsVisible] = useState(false);
	useScrollLock(isVisible);

	useEffect(() => {
		setIsVisible(!!sidepanelSlug);
	}, [sidepanelSlug]);

	const handleClose = useCallback(() => {
		setIsVisible(false);

		if (sidepanelSlug) {
			const timer = setTimeout(() => {
				window.history.pushState(null, '', pathname);
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [sidepanelSlug, pathname]);

	const { data: sidepanelData, status } = useSidePanel(sidepanelSlug, locale, preloadedSidepanel);

	// Freeze displayed state while dismissing: when the slug is cleared after the close animation
	// starts, React Query disables the query and resets status to 'pending', causing a skeleton flash.
	const displayRef = useRef<{ data: typeof sidepanelData; status: typeof status }>({
		data: undefined,
		status: 'pending',
	});
	if (sidepanelSlug) {
		displayRef.current = { data: sidepanelData, status };
	}
	const { data: displayData, status: displayStatus } = displayRef.current;

	return (
		<DismissableLayer isVisible={isVisible} onDismiss={handleClose} data-tag-page-section="overlay">
			<section className={styles.Sidepanel} data-open={isVisible} data-tag-page-section="sidepanel">
				<header className={styles.header}>
					{match(displayStatus)
						.with('error', () => <p />)
						.with('pending', () => <SidepanelHeaderSkeleton />)
						.with('success', () => <Heading tag="h2">{displayData?.title}</Heading>)
						.exhaustive()}

					<button
						aria-label={terms.sidepanel.closeScreenRead}
						type="button"
						className={styles.closeIconButton}
						onClick={handleClose}
					>
						<IconCloseWhite size="100" />
					</button>
				</header>
				<main className={styles.content}>
					{match(displayStatus)
						.with('error', () => <SidepanelEmptyFallback />)
						.with('pending', () => <SidepanelContentSkeleton />)
						.with('success', () => <RichText content={displayData?.content ?? null} />)
						.exhaustive()}
				</main>
			</section>
		</DismissableLayer>
	);
}

function SidepanelEmptyFallback() {
	const { terms } = useI18n();

	return (
		<>
			<div className="marginBottom200">
				<Flex direction="row" justify="center">
					<Drakkar size="750" />
				</Flex>
			</div>

			<div className="marginBottom100">
				<Heading tag="h2" className="marginBottom100">
					{terms.sidepanel.notFoundTitle}
				</Heading>
			</div>
			<Paragraph>{terms.sidepanel.notFoundSubtitle}</Paragraph>
		</>
	);
}
